import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { resolveImg } from "@/lib/api";
import { money, STATUS_STYLES, STATUSES, CATEGORIES } from "@/lib/campaignMeta";
import BoatFormDialog from "@/pages/BoatFormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Search, Sparkles, Pencil, Trash2, Gauge, MapPin, Anchor } from "lucide-react";
import { toast } from "sonner";

export default function Inventory() {
  const navigate = useNavigate();
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/boats").then(({ data }) => setBoats(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const filtered = useMemo(() => {
    return boats.filter((b) => {
      const text = `${b.year} ${b.manufacturer} ${b.model}`.toLowerCase();
      if (q && !text.includes(q.toLowerCase())) return false;
      if (cat !== "all" && b.category !== cat) return false;
      if (status !== "all" && b.status !== status) return false;
      return true;
    });
  }, [boats, q, cat, status]);

  const onSaved = () => { setDialogOpen(false); setEditing(null); load(); };
  const confirmDelete = async () => {
    try {
      await api.delete(`/boats/${deleteId}`);
      toast.success("Boat deleted");
      setDeleteId(null);
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="inventory-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-navy">Inventory</h1>
          <p className="text-muted-foreground mt-1">{boats.length} boats in your showroom</p>
        </div>
        <Button onClick={() => { setEditing(null); setDialogOpen(true); }} className="bg-navy hover:bg-navy-light text-white h-11" data-testid="add-boat-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Boat
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by make or model…" className="pl-9 h-11 bg-white" data-testid="inventory-search" />
        </div>
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-full md:w-52 h-11 bg-white" data-testid="inventory-category-filter"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full md:w-44 h-11 bg-white" data-testid="inventory-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-muted-foreground">Loading inventory…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-muted-foreground bg-white rounded-2xl border border-border">
          <Anchor className="w-12 h-12 mx-auto mb-4 opacity-30" />
          <p className="font-semibold text-navy">No boats found</p>
          <p className="text-sm">Try adjusting filters or add a new boat.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((b) => (
            <div key={b.id} className="bg-white rounded-2xl border border-border overflow-hidden card-hover group" data-testid={`boat-card-${b.id}`}>
              <div className="relative h-52 bg-secondary overflow-hidden">
                {b.photos?.[0] ? (
                  <img src={resolveImg(b.photos[0])} alt={b.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground"><Anchor className="w-10 h-10 opacity-30" /></div>
                )}
                <span className={`absolute top-3 right-3 text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full border ${STATUS_STYLES[b.status] || ""}`} data-testid={`boat-status-${b.id}`}>
                  {b.status}
                </span>
                {b.oem_support_available && (
                  <span className="absolute top-3 left-3 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-gold text-navy" data-testid={`boat-oem-support-${b.id}`}>
                    {b.promotional_boost_active ? "Promotional Boost" : "OEM Support Available"}
                  </span>
                )}
              </div>
              <div className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-xs text-muted-foreground uppercase tracking-wide">{b.year} · {b.category}</div>
                    <h3 className="font-bold text-lg text-navy leading-tight">{b.manufacturer} {b.model}</h3>
                  </div>
                  <div className="text-gold font-mono font-bold text-lg whitespace-nowrap">{money(b.price)}</div>
                </div>
                <div className="flex flex-wrap gap-2 mt-3 text-xs">
                  {(b.engine_manufacturer || b.horsepower || b.engine_count) && (
                    <Badge variant="secondary" className="bg-navy/5 text-navy font-normal"><Gauge className="w-3 h-3 mr-1" />{b.engine_count ? `${b.engine_count}× ` : ""}{b.engine_manufacturer} {b.engine_model} {b.horsepower ? `· ${b.horsepower} HP` : ""}</Badge>
                  )}
                  {b.location && <Badge variant="secondary" className="bg-navy/5 text-navy font-normal"><MapPin className="w-3 h-3 mr-1" />{b.location}</Badge>}
                </div>
                <div className="flex gap-2 mt-5">
                  <Button onClick={() => navigate(`/create-campaign?boatId=${b.id}`)} className="flex-1 gold-gradient text-navy font-bold" data-testid={`create-campaign-${b.id}`}>
                    <Sparkles className="w-4 h-4 mr-1.5" /> Create Campaign
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => { setEditing(b); setDialogOpen(true); }} data-testid={`edit-boat-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="outline" size="icon" onClick={() => setDeleteId(b.id)} className="hover:text-red-600 hover:border-red-300" data-testid={`delete-boat-${b.id}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <BoatFormDialog open={dialogOpen} onOpenChange={setDialogOpen} boat={editing} onSaved={onSaved} />

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this boat?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the boat from your inventory. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
